import React, { useMemo, useEffect } from 'react'
import {
	Card,
	Row,
	Col,
	Select,
	Button,
	Switch,
	Typography,
	message,
	Tabs,
	Input,
} from 'antd'
import { useForm, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { skipToken } from '@reduxjs/toolkit/query/react'
import { LanguageEnum } from 'modules/common/enums'
import {
	useGetNotificationTemplateQuery,
	useUpsertTemplateMutation,
} from 'modules/push-notifications/api'

export enum NotificationTypeEnum {
	AUTHOR = 'author',
	SOUNDTRACK = 'soundtrack',
	CATEGORY = 'category',
	RAMBAM = 'rambam',
	DAILY_STUDIES = 'dailyStudies',
	SUGGESTION = 'suggestion',
	NEW_CYCLE = 'newCycle',
}

const { Option } = Select
const { Title } = Typography
const { TabPane } = Tabs

const PLACEHOLDERS: Record<NotificationTypeEnum, string[]> = {
	author: ['nameEnglish', 'nameHebrew'],
	category: ['name'],
	soundtrack: ['title'],
	rambam: [],
	dailyStudies: [],
	suggestion: ['categoryTitle'],
	newCycle: [],
}

const PLACEHOLDER_LABELS: Record<string, string> = {
	nameEnglish: 'Name (English)',
	nameHebrew: 'Name (Hebrew)',
	name: 'Category Name',
	title: 'Soundtrack Title',
	categoryTitle: 'Category Title',
}

const CONTENT_TYPE_LABELS: Record<NotificationTypeEnum, string> = {
	[NotificationTypeEnum.AUTHOR]: 'Author',
	[NotificationTypeEnum.SOUNDTRACK]: 'Soundtrack',
	[NotificationTypeEnum.CATEGORY]: 'Category',
	[NotificationTypeEnum.RAMBAM]: 'Rambam',
	[NotificationTypeEnum.DAILY_STUDIES]: 'Daily Studies',
	[NotificationTypeEnum.SUGGESTION]: 'Suggestion',
	[NotificationTypeEnum.NEW_CYCLE]: 'New Cycle',
}

const NEW_CYCLE_LESSONS = [
	{ value: 'Tanya', label: 'Tanya Lesson' },
	{ value: 'Hayom', label: 'Hayom Daily' },
	{ value: 'Sicha', label: 'Sicha Study' },
	{ value: 'Guelah', label: 'Guelah Series' },
	{ value: 'Rambam', label: 'Rambam Series' },
]

type TemplateFormValues = {
	contentType: NotificationTypeEnum
	subType?: string
	language?: LanguageEnum
	enabled: boolean
	titleTemplate: string
	bodyTemplate: string
}

export const NotificationTemplateEditor: React.FC = () => {
	const { t } = useTranslation('pushNotifications', {
		keyPrefix: 'templateEditor',
	})
	const [upsertTemplate] = useUpsertTemplateMutation()

	const { control, reset, handleSubmit, setValue, getValues, watch } =
		useForm<TemplateFormValues>({
			defaultValues: {
				contentType: NotificationTypeEnum.AUTHOR,
				language: LanguageEnum.ENGLISH,
				enabled: true,
				titleTemplate: '',
				bodyTemplate: '',
				subType: undefined,
			},
		})

	const contentType = watch('contentType')
	const subType = watch('subType')
	const language = watch('language')

	useEffect(() => {
		if (contentType !== NotificationTypeEnum.NEW_CYCLE) {
			setValue('subType', undefined)
		}
		// Force no language for AUTHOR
		if (contentType === NotificationTypeEnum.AUTHOR) {
			setValue('language', undefined)
		}
	}, [contentType, setValue])

	const fetchContentType =
		contentType === NotificationTypeEnum.NEW_CYCLE && subType === 'Rambam'
			? NotificationTypeEnum.RAMBAM
			: contentType

	const fetchSubType =
		contentType === NotificationTypeEnum.NEW_CYCLE && subType === 'Rambam'
			? undefined
			: subType

	const fetchLanguage =
		contentType === NotificationTypeEnum.AUTHOR ? undefined : language

	const fetchKey = useMemo(
		() => `${fetchContentType}-${fetchLanguage || ''}-${fetchSubType || ''}`,
		[fetchContentType, fetchLanguage, fetchSubType]
	)

	const {
		data: templateData,
		isFetching,
		refetch,
	} = useGetNotificationTemplateQuery(
		fetchKey
			? {
					contentType: fetchContentType,
					language: fetchLanguage,
					subType: fetchSubType,
			  }
			: skipToken
	)

	useEffect(() => {
		if (!isFetching && templateData) {
			const uiContentType =
				templateData.contentType === NotificationTypeEnum.RAMBAM
					? NotificationTypeEnum.NEW_CYCLE
					: templateData.contentType

			const uiSubType =
				templateData.contentType === NotificationTypeEnum.RAMBAM
					? 'Rambam'
					: templateData.subType

			reset({
				contentType: uiContentType as NotificationTypeEnum,
				subType: uiSubType ?? undefined,
				language:
					templateData.contentType === NotificationTypeEnum.AUTHOR
						? undefined
						: templateData.language,
				enabled: templateData.enabled,
				titleTemplate: templateData.titleTemplate,
				bodyTemplate: templateData.bodyTemplate,
			})
		}
	}, [templateData, reset, isFetching])

	const insertPlaceholder = (
		placeholder: string,
		field: 'titleTemplate' | 'bodyTemplate'
	) => {
		const current = getValues(field)
		setValue(field, `${current}{{${placeholder}}}`)
	}

	const onSubmit = async (values: TemplateFormValues) => {
		const payload: TemplateFormValues = { ...values }
		if (
			values.contentType === NotificationTypeEnum.NEW_CYCLE &&
			values.subType === 'Rambam'
		) {
			payload.contentType = NotificationTypeEnum.RAMBAM
			payload.subType = undefined
		}

		// Drop language for AUTHOR before sending
		if (values.contentType === NotificationTypeEnum.AUTHOR) {
			payload.language = undefined
		}

		if (
			payload.contentType === NotificationTypeEnum.NEW_CYCLE &&
			!payload.subType
		) {
			message.error(t('selectLesson', 'Please select a lesson'))
			return
		}

		try {
			await upsertTemplate(payload).unwrap()
			message.success(t('saved', 'Template saved successfully'))
			refetch()
		} catch (err) {
			console.error(err)
			message.error(t('saveError', 'Failed to save template'))
		}
	}

	const resetForm = () => {
		refetch()
	}

	const handleSubTypeChange = (value: string) => {
		if (value === 'Rambam') {
			setValue('contentType', NotificationTypeEnum.RAMBAM)
			setValue('subType', undefined)
		} else {
			setValue('contentType', NotificationTypeEnum.NEW_CYCLE)
			setValue('subType', value)
		}
	}

	return (
		<Card>
			<Row gutter={[16, 16]}>
				<Col span={24}>
					<Title level={2}>{t('titlePage', 'Notification Templates')}</Title>
				</Col>

				{/* ContentType Dropdown */}
				<Col span={24} style={{ marginTop: 24 }}>
					<Controller
						name='contentType'
						control={control}
						render={({ field }) => (
							<Select {...field} style={{ width: 300 }}>
								{Object.values(NotificationTypeEnum)
									.filter(type => type !== NotificationTypeEnum.RAMBAM)
									.map(type => (
										<Option key={type} value={type}>
											{CONTENT_TYPE_LABELS[type]}
										</Option>
									))}
							</Select>
						)}
					/>
				</Col>

				{/* NEW_CYCLE SubType Dropdown */}
				{contentType === NotificationTypeEnum.NEW_CYCLE && (
					<Col span={12} style={{ marginTop: 24 }}>
						<Controller
							name='subType'
							control={control}
							rules={{ required: true }}
							render={({ field }) => (
								<Select
									{...field}
									style={{ width: '100%' }}
									placeholder={t('selectLesson', 'Select lesson')}
									onChange={handleSubTypeChange}
									value={field.value}
								>
									{NEW_CYCLE_LESSONS.map(lesson => (
										<Option key={lesson.value} value={lesson.value}>
											{lesson.label}
										</Option>
									))}
								</Select>
							)}
						/>
					</Col>
				)}

				{/* Language Tabs (hidden for AUTHOR) */}
				{contentType !== NotificationTypeEnum.AUTHOR && (
					<Col span={24} style={{ marginTop: 24 }}>
						<Tabs
							activeKey={language}
							onChange={key => setValue('language', key as LanguageEnum)}
							type='card'
						>
							{Object.values(LanguageEnum).map(lang => (
								<TabPane key={lang} tab={lang.toUpperCase()} />
							))}
						</Tabs>
					</Col>
				)}

				{/* Enabled Switch */}
				<Col span={24} style={{ marginTop: 24 }}>
					<Controller
						name='enabled'
						control={control}
						render={({ field }) => (
							<Switch checked={field.value} onChange={field.onChange} />
						)}
					/>{' '}
					{t('enabled')}
				</Col>

				{/* Title Template */}
				<Col span={24} style={{ marginTop: 24 }}>
					<Title level={5}>{t('title')}</Title>
					<Controller
						name='titleTemplate'
						control={control}
						render={({ field }) => (
							<Input {...field} placeholder={t('title')} />
						)}
					/>
					<div style={{ marginTop: 12 }}>
						{!!PLACEHOLDERS[contentType]?.length && (
							<Typography>Available placeholders</Typography>
						)}
						<div style={{ marginTop: 8 }}>
							{PLACEHOLDERS[contentType]?.map(p => (
								<Button
									key={p}
									size='small'
									type='dashed'
									style={{ marginRight: 8, marginBottom: 8 }}
									onClick={() => insertPlaceholder(p, 'titleTemplate')}
								>
									{PLACEHOLDER_LABELS[p] || p}
								</Button>
							))}
						</div>
					</div>
				</Col>

				{/* Body Template */}
				<Col span={24} style={{ marginTop: 24 }}>
					<Title level={5}>{t('body')}</Title>
					<Controller
						name='bodyTemplate'
						control={control}
						render={({ field }) => (
							<Input.TextArea {...field} rows={4} placeholder={t('body')} />
						)}
					/>
					<div style={{ marginTop: 12 }}>
						{!!PLACEHOLDERS[contentType]?.length && (
							<Typography>Available placeholders</Typography>
						)}
						<div style={{ marginTop: 8 }}>
							{PLACEHOLDERS[contentType]?.map(p => (
								<Button
									key={p}
									size='small'
									type='dashed'
									style={{ marginRight: 8, marginBottom: 8 }}
									onClick={() => insertPlaceholder(p, 'bodyTemplate')}
								>
									{PLACEHOLDER_LABELS[p] || p}
								</Button>
							))}
						</div>
					</div>
				</Col>

				{/* Save / Reset */}
				<Col span={24} style={{ marginTop: 24 }}>
					<Button type='primary' onClick={handleSubmit(onSubmit)}>
						{t('save')}
					</Button>
					<Button style={{ marginLeft: 8 }} onClick={resetForm}>
						{t('reset')}
					</Button>
				</Col>
			</Row>
		</Card>
	)
}
